import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { selectProductTiers } from '../../store/selectors/productsSelector';
import { selectIsLoadingByActionTypes } from '../../store/selectors/loadingSelectors';
import { ACCOUNT_SETTINGS_LOADING } from '../../store/actions/account/accountActionConstants';
import { PRODUCT_TIERS_LOADING } from '../../store/actions/products/productActionConstants';
import { fetchProductTiers } from '../../store/actions/products/productActions';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import SectionLoader from '../Loader/SectionLoader';
import { ReactComponent as Money } from '../../assets/images/svg/money.svg';
import { ReactComponent as Card } from '../../assets/images/svg/credit-card.svg';

const Table = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ hasCard }) =>
    hasCard ? '2fr 1fr 1fr' : '1.5fr 1fr'};
  // padding: ${pxToRem(10)} ${pxToRem(12)};
  padding: ${pxToRem(10)} 0;
  align-items: center;
  border-radius: 4px;
  margin-bottom: 8px;

  ${({ header }) =>
    header &&
    `
      font-size: 11px;
      font-weight: 500;
      color: ${themeColors.colorTextSecondary};
      background: none;
      margin-bottom: 0;
      padding: ${pxToRem(10)} 0;
    `}

  ${({ variant }) =>
    variant === 'firstRow' &&
    `
      background: ${themeColors.colorRowBgOne};
    `}

  ${({ variant }) =>
    variant === 'secondRow' &&
    `
      background: ${themeColors.colorRowBgTwo};
      border: 1px solid ${themeColors.colorRowBrTwo};
    `}

  ${({ variant }) =>
    variant === 'thirdRow' &&
    `
      background: ${themeColors.colorRowBgThree};
      border: 1px solid ${themeColors.colorRowBrThree};
    `}

    ${({ variant }) =>
    variant === 'fourthRow' &&
    `
      background: ${themeColors.colorRowBgFour};
      border: 1px solid ${themeColors.colorRowBrFour};
    `}
`;

const BestBadge = styled.span`
  background: ${themeColors.colorBestFlag};
  color: ${themeColors.colorWhite};
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  padding: 0 4px;
  margin-left: 6px;
  text-align: center;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
  font-weight: 500;
  justify-content: flex-end;

  &:first-child {
    justify-content: flex-start;
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: 6px;
  }
`;

const RowItem = styled.div`
  color: ${themeColors.colorTextPrimary};
  font-size: 14px;
  font-weight: 500;
  text-align: right;

  &:first-child {
    text-align: left;
    color: ${themeColors.colorTextSecondary};
    font-weight: 400;
    padding-left: 8px;
  }

  &:last-child {
    padding-right: 8px;
  }
`;

const PriceTierNote = styled.div`
  colr: ${themeColors.colorTextSecondary};
  text-align: center;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
`;

const formatValue = (input) => {
  const value = Number(input);
  if (value >= 1_000_000) {
    return `$${
      (value / 1_000_000) % 1 === 0
        ? value / 1_000_000
        : Math.round(value / 1_000_000)
    }M`;
  }
  if (value >= 1_000) {
    if (Math.round(value / 1_000) === 1000) return '$1M';
    return `$${Math.round(value / 1_000)}k`;
  }
  return `$${value}`;
};

const formatRange = (low, high) => {
  if (high === null) return `Over ${formatValue(low)}`;
  if (low === 0) return `Up to ${formatValue(high)}`;
  return `${formatValue(low)} to ${formatValue(high)}`;
};

const VolumeDiscountPriceTiers = ({
  symbolCode,
  location,
  isPostPaidVisible,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const productTiers = useSelector(selectProductTiers);

  const isLoading = useSelector(
    selectIsLoadingByActionTypes([
      ACCOUNT_SETTINGS_LOADING,
      PRODUCT_TIERS_LOADING,
    ]),
  );

  useEffect(() => {
    // if (symbol && isDeliveryLocationSymbolAvailable) {
    dispatch(
      fetchProductTiers({
        accountUid,
        symbol: symbolCode,
        side: 'Buy',
        location,
        trueTiers: true,
      }),
    );
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch]);

  const getVariantByPosition = (i) => {
    if (i === 0) return 'firstRow';
    if (i === 1) return 'secondRow';
    if (i === 2) return 'thirdRow';
    return 'fourthRow';
  };

  return (
    <SectionLoader isLoading={isLoading}>
      <Table>
        <Row header hasCard={isPostPaidVisible}>
          <HeaderTitle>Price tiers</HeaderTitle>
          <HeaderTitle>
            {isPostPaidVisible ? (
              <>
                <Money width={20} height={20} />
                Cash
              </>
            ) : (
              'Price Per Unit'
            )}
          </HeaderTitle>
          {isPostPaidVisible && (
            <HeaderTitle>
              <Card width={20} height={20} />
              Card
            </HeaderTitle>
          )}
        </Row>

        {productTiers?.Tiers?.map((tier, index) => {
          const isLast = index === productTiers?.Tiers?.length - 1;

          return (
            <Row
              hasCard={isPostPaidVisible}
              variant={getVariantByPosition(index, productTiers?.Tiers?.length)}
            >
              <RowItem>
                {formatRange(tier.LowBound, tier.HighBound)}
                {isLast && <BestBadge>Best</BestBadge>}
              </RowItem>
              <RowItem>${tier.PricePerUnit.toFixed(2)}</RowItem>
              {isPostPaidVisible && (
                <RowItem>${tier.BuyCCPricePerUnit.toFixed(2)}</RowItem>
              )}
            </Row>
          );
        })}
      </Table>
      <PriceTierNote>{t('buyWizard.priceTiersNote')}</PriceTierNote>
    </SectionLoader>
  );
};

VolumeDiscountPriceTiers.propTypes = {
  symbolCode: PropTypes.string,
  location: PropTypes.string,
  isPostPaidVisible: PropTypes.bool,
};

export default VolumeDiscountPriceTiers;
